"use client";

import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    BsCheckCircle,
    BsCheckCircleFill,
    BsQrCodeScan,
    BsXCircleFill,
} from "react-icons/bs";
import { useWhiteLabel } from "~/app/providers";
import { Alert, AlertDescription } from "~/components/shadcn/Alert";
import { cn } from "~/utils/fontsConfig";

export enum CheckinState {
    ALREADY_CHECKED_IN = "ALREADY_CHECKED_IN",
    VALID = "VALID",
    INVALID = "INVALID",
}

const useBarcodeDetector = () => {
    const [detector, setDetector] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function initializeDetector() {
            try {
                // Check if native BarcodeDetector is available
                if (window.BarcodeDetector) {
                    const detector = new window.BarcodeDetector({
                        formats: ["qr_code"],
                    });
                    setDetector(detector);
                } else {
                    // Load polyfill if native API is not available (iOS)
                    const { BarcodeDetector } = await import(
                        "barcode-detector"
                    );
                    const detector = new BarcodeDetector({
                        formats: ["qr_code"],
                    });
                    setDetector(detector);
                }
            } catch (err) {
                setError("Failed to initialize barcode detector");
                console.error("Barcode detector initialization error:", err);
            } finally {
                setIsLoading(false);
            }
        }

        initializeDetector();
    }, []);

    return { detector, isLoading, error };
};

const BarcodeScanner = ({
    onCodeScanned,
    toggleScanner,
}: {
    onCodeScanned: (code: string) => Promise<CheckinState | undefined>;
    toggleScanner: () => void;
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const rafIdRef = useRef<number>(null);
    const { detector, isLoading, error } = useBarcodeDetector();
    const [scanError, setScanError] = useState<string | null>(null);
    const [scanning, setScanning] = useState(false);
    const [scannedCode, setScannedCode] = useState<string | null>(null);
    const [scannedCodeState, setScannedCodeState] = useState<
        CheckinState | undefined
    >(undefined);
    const customization = useWhiteLabel()?.customization;
    const scan = useCallback(async () => {
        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

        if (
            !videoRef.current ||
            !detector ||
            videoRef.current.readyState !== videoRef.current.HAVE_ENOUGH_DATA
        ) {
            rafIdRef.current = requestAnimationFrame(scan);
            return;
        }

        try {
            const barcodes = await detector.detect(videoRef.current);

            if (barcodes.length > 0) {
                const newCode = barcodes[0].rawValue;
                const state = await onCodeScanned(newCode);
                if (state) {
                    setScannedCode(newCode);
                    setScannedCodeState(state);
                }
            }

            setScanError(null);
        } catch (error) {
            console.error("Scanning error:", error);
            setScanError("An error occurred while scanning. Please try again.");
        }

        if (scanning) rafIdRef.current = requestAnimationFrame(scan);
    }, [detector, scanning]);

    const stopMediaStream = useCallback(() => {
        console.log("stopping media stream");
        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const startScanning = useCallback(async () => {
        if (!videoRef.current) {
            console.error("Video element is not available");
            setScanError(
                "Failed to initialize video element. Please reload the page.",
            );
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" },
            });

            videoRef.current.srcObject = stream;
            streamRef.current = stream;

            videoRef.current.onloadedmetadata = () => {
                if (videoRef.current) {
                    videoRef.current.play();
                    setScanning(true);
                    scan();
                }
            };
        } catch (err) {
            console.error("Error accessing camera:", err);
            setScanError(
                "Failed to access the camera. Please make sure you've granted the necessary permissions.",
            );
        }
    }, [scan]);

    useEffect(() => {
        if (!isLoading && !error) {
            startScanning();
        }
        return () => {
            stopMediaStream();
        };
    }, [isLoading, error, startScanning, stopMediaStream]);

    return (
        <div className="flex flex-col items-center justify-center gap-2 p-4">
            {!scannedCode && (
                <div
                    className={cn(
                        "flex items-center gap-2 text-left text-sm",
                        customization?.labelClassName || "text-white",
                    )}
                >
                    <BsQrCodeScan className="h-4 w-4" />
                    Scan QR Code to check in guest
                </div>
            )}

            {scannedCode && (
                <div className="mt-4 text-center text-sm text-white/70">
                    {scannedCodeState === CheckinState.VALID && (
                        <span className="flex items-center justify-center gap-2">
                            <BsCheckCircleFill className="text-green-500" />
                            Successfully checked in
                        </span>
                    )}
                    {scannedCodeState === CheckinState.ALREADY_CHECKED_IN && (
                        <span className="flex items-center justify-center gap-2">
                            <BsCheckCircle className="text-yellow-500" />
                            Already checked in
                        </span>
                    )}
                    {scannedCodeState === CheckinState.INVALID && (
                        <span className="flex items-center justify-center gap-2">
                            <BsXCircleFill className="text-red-500" />
                            Invalid QR code
                        </span>
                    )}
                </div>
            )}

            {/* Camera View */}
            <div className="relative aspect-[3/4] w-full max-w-[400px] rounded-2xl bg-black">
                <video
                    ref={videoRef}
                    className="h-full w-full rounded-2xl object-cover "
                    autoPlay
                    playsInline
                    muted
                />

                {/* Scanning Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="z-50 aspect-[4/4] h-auto w-[80%] rounded-2xl border-2 border-dashed border-white/50" />
                </div>

                {/* Close button */}
                <button
                    className="absolute right-3 top-3 rounded-full bg-black/50 p-2 text-white"
                    onClick={toggleScanner}
                >
                    <BsXCircleFill className="h-6 w-6" />
                </button>
            </div>

            {/* Error Messages */}
            {(error || scanError) && (
                <Alert
                    variant="destructive"
                    className="mt-4 w-full max-w-[300px]"
                >
                    <AlertDescription>{error || scanError}</AlertDescription>
                </Alert>
            )}
        </div>
    );
};

export default BarcodeScanner;
